import { createBrowserRouter, Navigate } from "react-router";
import { RequireAuth } from "@repo/auth";
import { AppLayout } from "./shell/AppLayout";
import { VenueShellLayout } from "./shell/VenueShellLayout";
import { RequireVenueRole } from "./shell/RequireVenueRole";
import { RequireEntitlement } from "./shell/RequireEntitlement";
import { RequireGuaranteePolicy } from "./shell/RequireGuaranteePolicy";
import { Login } from "./pages/Login";
import { LoggedOut } from "./pages/LoggedOut";
import { Signup } from "./pages/Signup";
import { Activate } from "./pages/Activate";
import { SelectVenue } from "./pages/SelectVenue";
import { RootRedirect } from "./pages/RootRedirect";
import { VenueOverview } from "./pages/VenueOverview";
import { BookingsPage } from "./bookings/BookingsPage";
import { ScheduleSettings } from "./features/schedule/ScheduleSettings";
import { PlaceMatchPage } from "./features/place-match/PlaceMatchPage";
import { SettingsLayout } from "./features/settings/SettingsLayout";
import { SettingsGeneral } from "./features/settings/SettingsGeneral";
import { GuaranteePolicySettings } from "./features/settings/GuaranteePolicySettings";
import { ManagerPinSettings } from "./features/settings/ManagerPinSettings";
import { BookingChannelsPage } from "./features/channels/BookingChannelsPage";
import { LiveServicePage } from "./live/LiveServicePage";
import { DoorView } from "./live/DoorView";
import { WaitlistView } from "./waitlist/WaitlistView";
import { FloorPlanEditor } from "./features/floor-plan/FloorPlanEditor";
import { OnboardingWizard } from "./features/onboarding/OnboardingWizard";
import { Media } from "./pages/Media";
import { SiteEditor } from "./features/site/SiteEditor";
import { GuestDirectoryPage } from "./features/guests/GuestDirectoryPage";
import { BlacklistPage } from "./features/guests/BlacklistPage";
import { AbuseBlocksPanel } from "./features/abuse/AbuseBlocksPanel";
import { BillingPage } from "./features/billing/BillingPage";
import { DepositReconciliationPage } from "./features/deposits/DepositReconciliationPage";
import { FailedChargesPage } from "./features/noShowCharge/FailedChargesPage";
import { AnalyticsDashboard } from "./features/analytics/AnalyticsDashboard";
import { RequireOperator } from "./features/ops/RequireOperator";
import { OpsLayout } from "./features/ops/OpsLayout";
import { OpsSearchPage } from "./features/ops/OpsSearchPage";
import { TenantRecordPage } from "./features/ops/TenantRecordPage";
import { RouteErrorBoundary } from "./pages/RouteErrorBoundary";

/**
 * Venue-scoped SPA routing (staff-app-shell). One [AppLayout] wraps every route; `/login` + `/signup`
 * + `/activate` are public, everything else is behind `RequireAuth`. The venue lives in the path
 * (`/v/:venueId`) so it is deep-linkable. Feature screens mount as children of [VenueShellLayout],
 * guarded by [RequireVenueRole].
 *
 * Venue CONFIGURATION lives under a `settings` layout route ([SettingsLayout]) — MANAGER-gated once at
 * the layout — so the primary rail stays short (see shell/nav.ts). Screens that used to sit at the top
 * level (`floor-plan`, `media`, `onboarding`) moved under `settings/*`; their old paths redirect. The
 * booking-widget + Meta + Google surfaces merged into one `settings/channels` tabbed page.
 */
export const router = createBrowserRouter([
  {
    element: <AppLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: "/",
        element: (
          <RequireAuth>
            <RootRedirect />
          </RequireAuth>
        ),
      },
      // No login PAGE — RequireAuth forwards unauthenticated users straight to Keycloak (→ Google). This
      // route is an invisible redirect handler kept only for the `?idp=<alias>` deep-link (operators →
      // hidden Entra IdP); a plain hit just forwards to the default provider.
      { path: "/login", element: <Login /> },
      // PUBLIC post-logout landing — Keycloak's `post_logout_redirect_uri` returns here. Must stay
      // outside RequireAuth and must not auto-login: otherwise the straight-to-Google flow would
      // silently re-authenticate against the still-live Google session and logout would be a no-op.
      { path: "/logged-out", element: <LoggedOut /> },
      // PUBLIC, like /login: a prospective owner arrives with no session. Also where an
      // authenticated-but-unprovisioned user is sent (RootRedirect), so it must not sit behind
      // RequireAuth — that would bounce the first visit to /login and lose the sign-up entry point.
      { path: "/signup", element: <Signup /> },
      { path: "/activate", element: <Activate /> },
      {
        path: "/select-venue",
        element: (
          <RequireAuth>
            <SelectVenue />
          </RequireAuth>
        ),
      },
      {
        path: "/v/:venueId",
        element: (
          <RequireAuth>
            <VenueShellLayout />
          </RequireAuth>
        ),
        children: [
          { index: true, element: <Navigate to="overview" replace /> },
          { path: "overview", element: <VenueOverview /> },
          // — Service (day of service, WAITER-gated; server re-enforces every command) —
          {
            path: "bookings",
            element: (
              <RequireVenueRole minRole="WAITER">
                <BookingsPage />
              </RequireVenueRole>
            ),
          },
          {
            path: "live",
            element: (
              <RequireVenueRole minRole="WAITER">
                <LiveServicePage />
              </RequireVenueRole>
            ),
          },
          {
            // The phone-first door view — next arrivals + walk-in creation, reachable from /live.
            path: "door",
            element: (
              <RequireVenueRole minRole="WAITER">
                <DoorView />
              </RequireVenueRole>
            ),
          },
          {
            path: "waitlist",
            element: (
              <RequireVenueRole minRole="WAITER">
                <RequireEntitlement feature="WAITLIST">
                  <WaitlistView />
                </RequireEntitlement>
              </RequireVenueRole>
            ),
          },
          {
            // Hours & services: waiters VIEW (read-only), managers edit (controls gate on MANAGER,
            // server re-enforces MANAGE_VENUE_CONFIG). Operational — lives in the Service rail at
            // top-level `schedule` (moved from `settings/schedule`).
            path: "schedule",
            element: (
              <RequireVenueRole minRole="WAITER">
                <ScheduleSettings />
              </RequireVenueRole>
            ),
          },
          {
            // Per-venue guest directory (§C.8). WAITER-gated view; tag/note writes are CRM_NOTES_TAGS-
            // gated at the server. The diner never learns their status (GU-4).
            path: "guests",
            element: (
              <RequireVenueRole minRole="WAITER">
                <GuestDirectoryPage />
              </RequireVenueRole>
            ),
          },
          // — Gestion (back office, MANAGER) —
          {
            // Manager blacklist (create/renew/remove, review-to-renew R5). Server re-enforces R5 + the
            // step-up on override.
            path: "blacklist",
            element: (
              <RequireVenueRole minRole="MANAGER">
                <BlacklistPage />
              </RequireVenueRole>
            ),
          },
          {
            // Cross-venue analytics — read-only group-account metric comparison. MANAGER-gated (R2/R4);
            // the server resolves the role scope so only visible venues appear.
            path: "analytics",
            element: (
              <RequireVenueRole minRole="MANAGER">
                <AnalyticsDashboard />
              </RequireVenueRole>
            ),
          },
          {
            // Billing & plans (§C.3, R1). MANAGER view; WRITE controls render only for the account root
            // and the server re-enforces R1.
            path: "billing",
            element: (
              <RequireVenueRole minRole="MANAGER">
                <BillingPage />
              </RequireVenueRole>
            ),
          },
          {
            // Deposit & prepayment reconciliation (R6/R7/R9/R10) + manager refund. Server re-enforces
            // MANAGE_VENUE_CONFIG (a waiter cannot refund). Guarded on deposit capture being live (it is not
            // yet — the surface stays unreachable until that build ships).
            path: "deposits",
            element: (
              <RequireVenueRole minRole="MANAGER">
                <RequireGuaranteePolicy surface="deposit">
                  <DepositReconciliationPage />
                </RequireGuaranteePolicy>
              </RequireVenueRole>
            ),
          },
          {
            // Failed no-show charges (R10): declined/expired guarantees surfaced for manager follow-up.
            // Only reachable once the venue no-show guarantee is enabled (settings → Garantie).
            path: "guarantees/failed",
            element: (
              <RequireVenueRole minRole="MANAGER">
                <RequireGuaranteePolicy surface="noShow">
                  <FailedChargesPage />
                </RequireGuaranteePolicy>
              </RequireVenueRole>
            ),
          },
          {
            // "Mon site web" — the public storefront editor, promoted to a top-level Gestion surface
            // (nav.ts). Renders with its own PageScaffold chrome (not the settings sub-nav). The old
            // settings/site link redirects here.
            path: "site",
            element: (
              <RequireVenueRole minRole="MANAGER">
                <SiteEditor />
              </RequireVenueRole>
            ),
          },
          // — Settings hub (venue configuration). MANAGER-gated once here; children need no own guard
          //   except the FLOOR_PLAN entitlement. Sub-nav is shell/nav.ts SETTINGS_SECTIONS. —
          {
            path: "settings",
            element: (
              <RequireVenueRole minRole="MANAGER">
                <SettingsLayout />
              </RequireVenueRole>
            ),
            children: [
              { index: true, element: <Navigate to="general" replace /> },
              { path: "general", element: <SettingsGeneral /> },
              {
                path: "floor-plan",
                element: (
                  <RequireEntitlement feature="FLOOR_PLAN">
                    <FloorPlanEditor />
                  </RequireEntitlement>
                ),
              },
              { path: "media", element: <Media /> },
              { path: "guarantee", element: <GuaranteePolicySettings /> },
              { path: "pin", element: <ManagerPinSettings /> },
              // Moved to the top-level "Mon site web" (Gestion). Redirect old /settings/site bookmarks
              // up two segments to /v/:venueId/site.
              { path: "site", element: <Navigate to="../../site" replace /> },
              // Canaux de réservation — merged Widget (EmbedSettings) / Réseaux (MetaReserveSettings) /
              // Google (PlaceMatchPage) tabbed page.
              { path: "channels", element: <BookingChannelsPage /> },
              { path: "abuse-blocks", element: <AbuseBlocksPanel /> },
              // Re-openable RwG match; also mounted as the onboarding match step and the channels Google tab.
              { path: "place-match", element: <PlaceMatchPage /> },
              // Deep-link redirects for the two channel screens merged into the tabbed page.
              { path: "embed", element: <Navigate to="../channels?tab=widget" replace /> },
              { path: "meta-reserve", element: <Navigate to="../channels?tab=social" replace /> },
            ],
          },
          {
            // The onboarding wizard: managers drive setup to READY. Stays a top-level full-screen flow
            // (its internal steps deep-link to schedule / settings pages), listed in the Settings hub
            // sub-nav ("Mise en route"). MANAGER-gated; server re-enforces R5.
            path: "onboarding",
            element: (
              <RequireVenueRole minRole="MANAGER">
                <OnboardingWizard />
              </RequireVenueRole>
            ),
          },
          // — Deep-link redirects for screens moved under the Settings hub —
          { path: "floor-plan", element: <Navigate to="../settings/floor-plan" replace /> },
          { path: "media", element: <Navigate to="../settings/media" replace /> },
        ],
      },
      {
        // The operator back office (super-admin.md): a top-level area, NOT venue-scoped, gated on the
        // `platform-operator` realm role. Search → tenant record; impersonation flips into venue context.
        path: "/ops",
        element: (
          <RequireAuth>
            <RequireOperator>
              <OpsLayout />
            </RequireOperator>
          </RequireAuth>
        ),
        children: [
          { index: true, element: <OpsSearchPage /> },
          { path: "tenants/:tenantId", element: <TenantRecordPage /> },
        ],
      },
    ],
  },
]);
